/**
 * Image path utilities for handling base URL configuration
 *
 * Ensures all images work correctly with subdirectory deployment
 * (e.g., /kimrosecenter/ prefix)
 */

import { getBaseUrl } from './routing';

/**
 * Get the full URL for an image path, including the base URL prefix
 *
 * @param imagePath - Relative path to image (e.g., '/images/logo.png')
 * @returns Full path with base URL prefix
 *
 * @example
 * // With VITE_BASE_URL='/kimrosecenter/'
 * getImageUrl('/images/logo.png')
 * // Returns: '/kimrosecenter/images/logo.png'
 *
 * @example
 * // With VITE_BASE_URL='/'
 * getImageUrl('/images/logo.png')
 * // Returns: '/images/logo.png'
 */
export function getImageUrl(imagePath: string): string {
  const baseUrl = getBaseUrl();

  // If no path provided, return base URL
  if (!imagePath) {
    return baseUrl;
  }

  // Remove leading slash from image path if base URL ends with slash
  let cleanPath = imagePath;
  if (baseUrl.endsWith('/') && imagePath.startsWith('/')) {
    cleanPath = imagePath.slice(1);
  }

  // Add leading slash to path if base URL doesn't end with slash
  if (!baseUrl.endsWith('/') && !imagePath.startsWith('/')) {
    cleanPath = '/' + imagePath;
  }

  return baseUrl + cleanPath;
}

/**
 * Check if an image path is already absolute (external URL)
 *
 * @param imagePath - Image path to check
 * @returns True if path is absolute/external
 */
export function isAbsoluteUrl(imagePath: string): boolean {
  return /^(https?:)?\/\//i.test(imagePath);
}

/**
 * Get image URL, handling both relative and absolute paths
 *
 * @param imagePath - Image path (relative or absolute)
 * @returns Full URL with base prefix if relative, or original URL if absolute
 */
export function getImageUrlSafe(imagePath: string | undefined): string {
  if (!imagePath) {
    return '';
  }

  // If already absolute URL, return as-is
  if (isAbsoluteUrl(imagePath)) {
    return imagePath;
  }

  // Otherwise, add base URL prefix
  return getImageUrl(imagePath);
}
